import { buildSiteTree, type Site, type SiteNode, type Vehicle } from "@fts/core";
import { useMemo, type ReactNode } from "react";
import { EmptyState } from "../components/ui";
import { useI18n } from "../i18n";
import { vehiclesUnder } from "../lib/fleet";

type Row = { node: SiteNode; depth: number };

function walk(nodes: SiteNode[], depth: number, out: Row[]): Row[] {
  for (const n of nodes) {
    out.push({ node: n, depth });
    walk(n.children, depth + 1, out);
  }
  return out;
}

/** Sites in tree order with indentation; vehicle counts include descendants. */
export function SitesTable({ sites, vehicles, rootIds, emptyAction }: { sites: Site[]; vehicles: Vehicle[]; rootIds?: string[]; emptyAction?: ReactNode }) {
  const { t, locale } = useI18n();
  const rows = useMemo(() => {
    const tree = buildSiteTree(sites, vehicles);
    const roots = rootIds ? walk(tree, 0, []).filter((r) => rootIds.includes(r.node.id)).map((r) => r.node) : tree;
    return walk(roots, 0, []);
  }, [sites, vehicles, rootIds]);

  if (rows.length === 0)
    return (
      <EmptyState compact icon="building" title={t("sites.empty")} action={emptyAction}>
        {t("sites.emptyHint")}
      </EmptyState>
    );

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t("site.name")}</th>
            <th scope="col">{t("site.kind")}</th>
            <th scope="col" className="col-hide-sm">
              {t("site.address")}
            </th>
            <th scope="col" className="num col-hide-sm">
              {t("site.childCount")}
            </th>
            <th scope="col" className="num">
              {t("site.vehicles")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ node, depth }) => (
            <tr key={node.id}>
              <td>
                <span className="site-cell" style={{ ["--depth" as string]: depth }}>
                  {depth > 0 && <span className="site-branch" aria-hidden="true" />}
                  <a href={`#/sites/${encodeURIComponent(node.id)}`} className="row-link">
                    {node.name}
                  </a>
                </span>
              </td>
              <td>
                <span className="kind-tag">{t(`siteKind.${node.kind}`)}</span>
              </td>
              <td className="muted col-hide-sm">{node.address ?? t("common.empty")}</td>
              <td className="num col-hide-sm">{node.children.length.toLocaleString(locale)}</td>
              <td className="num">{vehiclesUnder(sites, vehicles, node.id).length.toLocaleString(locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
